import { Component, ElementRef, OnDestroy, ViewChild, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LanguageSwitcherComponent } from '../components/language-switcher/language-switcher.component';
import { AuthService } from '../services/auth.service';
import { AuthProvider, stashOAuthReturn, takeProviderError } from '../services/provider-error';
import { BrandService } from '../../services/brand-config.service';
import {
  SupportedLanguage,
  TranslationService,
} from '../services/translation.service';
import { TranslatePipe } from '../pipes/translate.pipe';
import { AppComponent } from '../app.component';
import { PlatformService } from '../services/platform.service';
import { SessionService } from '../services/session.service';
import { ClinicPublicConfig, ClinicService } from '../services/clinic.service';

@Component({
  selector: 'app-signup',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, MatProgressSpinner, LanguageSwitcherComponent, TranslatePipe],
  templateUrl: './signup.component.html',
  styleUrl: './signup.component.scss',
})
export class SignupComponent implements OnDestroy {
  isLoading = true;
  busy = false;
  email = '';
  password = '';
  fname = '';
  lname = '';
  errorMsg = '';
  confirmationSent = false;
  alreadyRegistered = false;
  currentLanguage: SupportedLanguage = 'en';
  signInUrl: string;
  signInQuery: Record<string, string> = {};

  // ─── white-labelled clinic signup (/:lang/signup/<slug>[?invite=]) ──────
  clinicSlug = '';
  clinic: ClinicPublicConfig | null = null;
  clinicLogo = '';
  inviteToken = '';
  inviteDoctorName = '';
  inviteInvalid = false;
  consent = false;

  /** Revital brief 29, option A (Danna 23.09): the email form starts folded
   *  behind "continue with email" and opens on demand. */
  showEmail = false;
  /** Set on the first submit so field hints only appear once she tried. */
  triedSubmit = false;

  @ViewChild('googleBtn') googleBtn?: ElementRef<HTMLDivElement>;

  constructor(
    private zone: NgZone,
    public auth: AuthService,
    public brand: BrandService,
    public translationService: TranslationService,
    public platform: PlatformService,
    private appComponent: AppComponent,
    private session: SessionService,
    private snackBar: MatSnackBar,
    private route: ActivatedRoute,
    private router: Router,
    private clinics: ClinicService,
  ) {
    const lang = this.appComponent.clerkLocalizationLang || 'en';
    this.clinicSlug = String(this.route.snapshot.params['clinic'] || '').toLowerCase();
    this.inviteToken = this.route.snapshot.queryParamMap.get('invite') || '';
    this.signInUrl = this.clinicSlug ? `/${lang}/login/${this.clinicSlug}` : `/${lang}/login`;
    if (this.inviteToken) this.signInQuery = { invite: this.inviteToken };
    const prefill = this.session.consumeAuthPrefill();
    if (prefill?.email) this.email = prefill.email;
    if (prefill?.password) this.password = prefill.password;
    if (this.email) this.showEmail = true;
    this.init();
  }

  /**
   * Correction brief 1.8. She reaches this screen after roughly eleven minutes
   * of work, and nothing on it said what happens to that work. The reassurance
   * is shown ONLY when there really is a finished check-in waiting (the guard
   * stored her results route before bouncing her here), because on a cold
   * signup the same sentence would be a lie.
   */
  get hasPendingCheckin(): boolean {
    const url = this.session.getRedirectUrl() || '';
    return /\/app\/(results|trial-result)\//.test(url.toLowerCase());
  }

  /**
   * The one provider that leads the page. Apple where the device is Apple
   * (native iOS, or Safari on an iPhone/iPad/Mac), Google everywhere else.
   * The other providers stay available, just smaller.
   */
  get primaryProvider(): 'apple' | 'google' {
    if (this.platform.isNative) return this.platform.isIOS ? 'apple' : 'google';
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    if (/iPhone|iPad|iPod/.test(ua)) return 'apple';
    const macSafari = /Macintosh/.test(ua) && /Safari/.test(ua) && !/Chrome|Chromium|Edg|Firefox/.test(ua);
    return macSafari ? 'apple' : 'google';
  }

  /** Apple sign-in is offered on iOS and on the web, never on native Android. */
  get offersApple(): boolean {
    return this.platform.isIOS || !this.platform.isNative;
  }

  get emailValid(): boolean {
    return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(this.email.trim());
  }

  get passwordOk(): boolean {
    return this.password.length >= 6;
  }

  openEmail(): void {
    this.showEmail = true;
    this.errorMsg = '';
  }

  private providerErrSub?: { unsubscribe(): void };

  private showProviderError(p: AuthProvider): void {
    this.busy = false;
    this.errorMsg = this.t(`auth.errors.${p}Failed`);
  }

  private async init() {
    // Revital brief 29: a provider that failed on the web (Google's own button,
    // or an Apple/Facebook redirect that came back with an error) says so here.
    const failed = takeProviderError();
    if (failed) this.showProviderError(failed);
    // Google's button calls back outside Angular's zone; paint inside it.
    this.providerErrSub = this.auth.providerError$.subscribe((p) => this.zone.run(() => this.showProviderError(p)));
    this.currentLanguage = await this.translationService.getCurrentLanguageAsync();
    if (this.clinicSlug) await this.initClinic();
    this.isLoading = false;
    if (!this.platform.isNative) {
      setTimeout(() => {
        if (this.googleBtn?.nativeElement) {
          this.auth.renderGoogleSignInButton(this.googleBtn.nativeElement);
        }
      }, 0);
    }
  }

  private t(key: string): string {
    return this.translationService.translate(key);
  }

  private async initClinic(): Promise<void> {
    const cfg = await this.clinics.enter(this.clinicSlug);
    if (!cfg || cfg.status !== 'active') {
      this.clinics.clearPending();
      return;
    }
    if (this.clinics.ensureBrandHost(cfg)) return;
    this.clinic = cfg;
    this.clinicLogo = this.clinics.logoUrl(cfg);
    const pending = this.clinics.peekPending();
    if (pending?.slug === cfg.slug && pending.consent) this.consent = true;
    if (this.inviteToken) {
      try {
        const preview = await this.clinics.getInvitePreview(this.inviteToken);
        this.inviteDoctorName = preview?.doctorName || '';
        if (preview?.patientEmail && !this.email) {
          this.email = preview.patientEmail;
          this.showEmail = true;
        }
      } catch {
        this.inviteInvalid = true;
      }
    }
  }

  ngOnDestroy(): void {
    this.providerErrSub?.unsubscribe();
    if (this.clinic) this.clinics.leave();
  }

  get isClinicPage(): boolean {
    return !!this.clinic;
  }

  get clinicName(): string {
    return this.clinics.displayName(this.clinic);
  }

  get needsConsent(): boolean {
    return !!this.clinic && !this.consent;
  }

  onConsentChange(checked: boolean): void {
    this.consent = checked;
    this.errorMsg = '';
    if (!this.clinic) return;
    if (checked) {
      this.clinics.setPending({
        slug: this.clinic.slug,
        invite: this.inviteInvalid ? null : this.inviteToken || null,
        consent: true,
      });
    } else {
      this.clinics.clearPending();
    }
  }

  private guardConsent(): boolean {
    if (!this.clinic) return true;
    if (!this.consent) {
      this.errorMsg = this.t('auth.login.clinicConsentRequired');
      return false;
    }
    this.onConsentChange(true);
    return true;
  }

  async submit() {
    if (this.busy) return;
    this.errorMsg = '';
    this.triedSubmit = true;
    if (!this.emailValid || !this.passwordOk) return;
    if (!this.guardConsent()) return;
    this.busy = true;
    try {
      const { error, data } = await this.auth.signUp(
        this.email.trim(),
        this.password,
        // `lang` lets the Supabase email template render in the user's UI
        // language (Go-template conditional on .Data.lang); see dashboard.
        // `signup_redirect`/`purpose` survive the email-confirmation round trip
        // in user metadata: when she opens the confirmation link in a DIFFERENT
        // browser/app (mail-app webview, other device), this browser's
        // localStorage redirectUrl + sessionStorage purpose don't travel — the
        // spinner restores them from metadata so she still lands on her results
        // instead of being dumped into a fresh test funnel.
        {
          first_name: this.fname.trim(),
          last_name: this.lname.trim(),
          lang: this.currentLanguage,
          signup_redirect: this.session.getRedirectUrl() || undefined,
          purpose: sessionStorage.getItem('purpose') || undefined,
        },
      );
      if (error) {
        this.errorMsg = error.message || this.t('auth.errors.signUpFailed');
        return;
      }
      if (data?.session) {
        return;
      }
      // Supabase anti-enumeration: signing up an email that already has a
      // confirmed account returns a 200 with an obfuscated user (empty
      // `identities`) and sends NO email. Without this check we'd show a false
      // "check your inbox" — the user then hunts for an email that never comes
      // and calls support.
      const identities = (data?.user as { identities?: unknown[] } | null)?.identities;
      if (Array.isArray(identities) && identities.length === 0) {
        // Account already exists. Rather than dead-ending on "already
        // registered", just log her straight in with the password she typed —
        // the same seamless "connect" the Google button gives. The auth-state
        // emit then drives the post-login routing (identical to LoginComponent).
        // Only if the password is wrong (or it's a social-only account with no
        // password) do we fall back to the sign-in / reset-password message.
        const { error: signInError } = await this.auth.signInWithPassword(
          this.email.trim(),
          this.password,
        );
        if (!signInError) return;
        this.alreadyRegistered = true;
        return;
      }
      this.confirmationSent = true;
    } finally {
      this.busy = false;
    }
  }

  /** Existing-account recovery: send the user a real, actionable reset email so
   *  they have something to find in their inbox. Mirrors login.forgotPassword. */
  async sendReset() {
    if (this.busy) return;
    this.busy = true;
    try {
      const { error } = await this.auth.resetPassword(this.email.trim());
      this.snackBar.open(
        error
          ? error.message || this.t('auth.errors.resetFailed')
          : this.translationService.translate('auth.signup.resetSent', { email: this.email }),
        'OK',
        { duration: 5000 },
      );
    } finally {
      this.busy = false;
    }
  }

  stashAuth() {
    this.session.setAuthPrefill({ email: this.email, password: this.password });
  }

  async google() {
    if (this.busy) return;
    this.errorMsg = '';
    if (!this.guardConsent()) return;
    this.busy = true;
    if (!this.platform.isNative) stashOAuthReturn('google');
    try {
      const { error } = await this.auth.signInWithGoogle();
      if (error) {
        // A1: hide the raw provider error behind friendly copy.
        this.errorMsg = this.t('auth.errors.googleFailed');
      }
    } finally {
      this.busy = false;
    }
  }

  async facebook() {
    if (this.busy) return;
    this.errorMsg = '';
    if (!this.guardConsent()) return;
    this.busy = true;
    if (!this.platform.isNative) stashOAuthReturn('facebook');
    try {
      const { error } = await this.auth.signInWithFacebook();
      if (error) {
        this.errorMsg = this.t('auth.errors.facebookFailed');
      }
    } finally {
      this.busy = false;
    }
  }

  async apple() {
    if (this.busy) return;
    this.errorMsg = '';
    if (!this.guardConsent()) return;
    this.busy = true;
    if (!this.platform.isNative) stashOAuthReturn('apple');
    try {
      const { error } = await this.auth.signInWithApple();
      if (error) {
        this.errorMsg = this.t('auth.errors.appleFailed');
      }
    } finally {
      this.busy = false;
    }
  }
}
